<template>
  <section
    class="booking-engine"
    :class="{ 'booking-engine--is-open': is_open }"
    ref="booking-engine"
    v-click-outside="close_engine"
  >

    <package-type-switch @change:package_type="on_package_type_change" />

    <b-row class="booking-engine__body" @click.self="close_engine">
      <!-- occupancy -->
      <b-col
        sm="12"
        :lg="!is_only_stay_package ? 3 : 4"
        class="booking-engine__column"
        :class="{ current: current_tab === 0 }"
        @click="current_tab = 0"
      >
        <booking-engine-occupancy
          :current_tab="current_tab"
          :is_current="current_tab === 0"
          :occupancy="occupancy"
          @scroll-into-view="scroll_into_view"
          @go-to-tab="go_to_tab"
          @occupancy-updated="on_occupancy_updated"
          @keyd-map-init="(val) => (keyd_map_init = val)"
        />
      </b-col>

      <!-- destinations -->
      <b-col
        sm="12"
        :lg="!is_only_stay_package ? 2 : 3"
        class="booking-engine__column"
        :class="{ current: current_tab === 1 }"
        @click="on_destination_click"
      >
        <booking-engine-destination
          :current_tab="current_tab"
          :is_current="current_tab === 1"
          :location="location"
          :keyd_map_init="keyd_map_init"
          :keydsmap="keydsmap"
          @scroll-into-view="scroll_into_view"
          @update:current_tab="(val) => (current_tab = val)"
          @submit-location="on_submit_location"
        />
      </b-col>

      <!-- airports (flight+stay only) -->
      <b-col
        v-if="!is_only_stay_package"
        sm="12"
        lg="3"
        class="booking-engine__column"
        :class="{ current: current_tab === 2 }"
        @click="current_tab = departure_airports.length > 0 ? 2 : -1"
      >
        <booking-engine-airports
          :current_tab="current_tab"
          :is_current="current_tab === 2"
          :departure_airports="departure_airports"
          :loading="loading"
          :departure_airports_no_results="departure_airports_no_results"
          :has_availabilities="has_availabilities"
          @scroll-into-view="scroll_into_view"
          @go-to-tab="go_to_tab"
          @airports-changed="get_availabilities"
        />
      </b-col>

      <!-- availability (flight+stay only) -->
      <b-col
        v-if="!is_only_stay_package"
        sm="12"
        lg="2"
        class="booking-engine__column"
        :class="{ current: current_tab === 3 }"
        @click="current_tab = has_availabilities ? 3 : -1"
      >
        <booking-engine-departure-picker
          :current_tab="current_tab"
          :is_current="current_tab === 3"
          :availabilities="availabilities"
          :departure_date="departure_date"
          @scroll-into-view="scroll_into_view"
          @go-to-tab="go_to_tab"
          @select-date="on_submit_date"
        />
      </b-col>

      <!-- date range picker: checkin/checkout (only-stay only) -->
      <b-col
        v-if="is_only_stay_package"
        sm="12"
        lg="3"
        class="booking-engine__column"
        :class="{ current: current_tab === 2 }"
        @click="current_tab = 2"
      >
        <booking-engine-date-range-picker
          :checkin_date.sync="checkin_date_synced"
          :checkout_date.sync="checkout_date_synced"
          :is_current="current_tab === 2"
          :min_checkin_date="min_checkin_date"
          :max_date="max_booking_date"
          :disclaimer="date_range_disclaimer"
          @dates-selected="close_engine"
        />
      </b-col>

      <!-- search button -->
      <b-col
        sm="12"
        lg="2"
        class="booking-engine__column d-flex align-self-end"
        @click="current_tab = -1"
      >
        <div
          class="booking-engine__search"
          :class="{ active: departure_date !== '' }"
        >
          <div class="vb-form text-center">
            <vb-button
              :key="searching"
              @click="search"
              :vb_options="{
                text: searching ? 'Attendi' : 'Cerca',
                disabled: search_button_disabled,
                variant: 'info',
              }"
            />
          </div>
        </div>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { get_config, custom_event } from "@utilities";
import { alpitour_rybbit_event, slim_search_info } from "@alpitour/utilities/rybbit";
import { has_valid_occupancy } from "@alpitour/utilities";
import PackageTypeSwitch from "./package-type-switch.vue";
import BookingEngineOccupancy from "./occupancy.vue";
import BookingEngineDestination from "./destination.vue";
import BookingEngineAirports from "./airports.vue";
import BookingEngineDeparturePicker from "./departure-picker.vue";
import BookingEngineDateRangePicker from "./date-range-picker.vue";
import {
  build_search_params,
  encode_search_params,
  get_airports_api,
  get_availabilities_api,
  calculateMinCheckinDate,
  calculateMaxDate,
} from "./logic";

// Distance (px) within which the booking engine is considered already
// aligned with scrollIntoView's resting position, so a focus doesn't
// re-trigger the scroll.
const SCROLL_ALIGNED_TOLERANCE = 16;

export default {
  name: "alpitour-booking-engine",
  components: {
    "package-type-switch": PackageTypeSwitch,
    "booking-engine-occupancy": BookingEngineOccupancy,
    "booking-engine-destination": BookingEngineDestination,
    "booking-engine-airports": BookingEngineAirports,
    "booking-engine-departure-picker": BookingEngineDeparturePicker,
    "booking-engine-date-range-picker": BookingEngineDateRangePicker,
  },
  props: {
    variant: {
      type: String,
      default: () => "default",
    },
    is_open: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    // global state keys
    ...mapState(["occupancy", "searching"]),
    // module state keys
    ...mapState("liveat", [
      "location",
      "departure_airports",
      "products",
      "statics",
      "keyds",
      "availabilities",
      "last_search",
      "current_search",
      "departure_date",
      "nights",
      "keydsmap",
      "checkin_date",
      "checkout_date",
      "package_type",
    ]),
    ...mapGetters("liveat", ["is_only_stay_package"]),
    flex_dates: {
      get() {
        return this.$store.state.liveat.flex_dates;
      },
      set(value) {
        this.$store.commit("liveat/flex_dates", value);
      },
    },
    search_params() {
      return build_search_params({
        package_type: this.package_type,
        occupancy: this.occupancy,
        location: this.location,
        nights: this.nights,
        departure_date: this.departure_date,
        flex_dates: this.flex_dates,
        departure_airports: this.departure_airports,
        brands: get_config().guiSettings.filterBrands
          ? get_config().guiSettings.filterBrands
          : [],
        checkin_date: this.checkin_date,
        checkout_date: this.checkout_date,
      });
    },
    has_availabilities() {
      return Object.values(this.availabilities).length > 0;
    },
    checkin_date_synced: {
      get() {
        return this.$store.state.liveat.checkin_date;
      },
      set(value) {
        this.$store.commit("liveat/checkin_date", value);
      },
    },
    checkout_date_synced: {
      get() {
        return this.$store.state.liveat.checkout_date;
      },
      set(value) {
        this.$store.commit("liveat/checkout_date", value);
      },
    },
    min_checkin_date() {
      return calculateMinCheckinDate();
    },
    max_booking_date() {
      return calculateMaxDate();
    },
    date_range_disclaimer() {
      return get_config().guiSettings.minDisclaimer || "";
    },
    search_button_disabled() {
      if (this.is_only_stay_package) {
        const { keyd: location_key } = this.location || {};
        return (
          this.checkin_date === "" ||
          this.checkout_date === "" ||
          !location_key ||
          location_key === -1 ||
          this.searching
        );
      }
      return this.departure_date === "" || this.searching;
    },
  },

  data() {
    return {
      current_tab: -1,
      departure_airports_no_results: false,
      loading: false,
      default_departure_date: "",
      last_departure_airports_selection: [],
      keyd_map_init: false,
    };
  },
  mounted() {
    window.eventBus.$off("suggestion");
    window.eventBus.$on("suggestion", () => {
      this.default_departure_date = this.departure_date;
      this.search();
      this.get_availabilities();
    });
    this.keyd_map_init = has_valid_occupancy(this.occupancy);
  },
  methods: {
    scroll_into_view() {
      const booking_engine = this.$refs["booking-engine"];
      if (!booking_engine) {
        return;
      }
      const { top } = booking_engine.getBoundingClientRect();
      if (Math.abs(top) <= SCROLL_ALIGNED_TOLERANCE) {
        return;
      }
      booking_engine.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    close_engine() {
      if (this.current_tab === 0 && !has_valid_occupancy(this.occupancy)) {
        return;
      }
      if (this.keydsmap.length == 0) return;
      this.current_tab = -1;
    },
    close_popovers() {
      this.$root.$emit("bv::hide::popover");
    },
    go_to_tab(target) {
      setTimeout(() => {
        this.current_tab = target;
      }, 1);
    },
    on_package_type_change() {
      this.current_tab = -1;
      this.departure_airports_no_results = false;
      this.last_departure_airports_selection = [];
      this.loading = false;
    },
    on_occupancy_updated(e) {
      this.last_departure_airports_selection = this.departure_airports; // mantengo ultima selezione aeroporti in caso di cambio occupancy
      this.$store.commit("liveat/reset_store_values", {
        keys: ["departure_airports", "availabilities", "keydsmap", "location"],
      });
      this.$store.commit("occupancy", e.occupancy);
      this.keyd_map_init = false;
    },
    encode_search_params_internal() {
      return encode_search_params(this.search_params);
    },
    get_airports() {
      if (this.departure_airports.length > 0)
        this.last_departure_airports_selection = this.departure_airports;
      this.$store.commit("liveat/reset_store_values", {
        keys: ["departure_airports", "availabilities"],
      });
      this.$store.commit(
        "liveat/current_search",
        this.encode_search_params_internal(),
      );
      this.departure_airports_no_results = false;
      this.loading = true;
      get_airports_api(this.location.keyd, this.occupancy[0]).then(
        (success) => {
          let airports = success.data.data.airports;
          let departure_airports = airports.map((a) => {
            return {
              ...a,
              selected:
                this.last_departure_airports_selection.filter((dep) => {
                  return dep.iata_code == a.iata_code && dep.selected;
                }).length > 0,
            };
          });
          this.$store.commit("liveat/departure_airports", departure_airports);
          // se c'è almeno un aeroporto già selezionato faccio partire subito la richiesta alle availabilities
          if (
            departure_airports.filter((a) => {
              return a.selected;
            }).length > 0
          )
            this.get_availabilities();
          if (departure_airports.length == 0)
            this.departure_airports_no_results = true;
          this.current_tab = 2;
        },
        (error) => {
          this.log("[booking-engine] get_airports error", error);
          this.departure_airports_no_results = true;
        },
      ).finally(() => {
        this.loading = false;
      });
    },
    get_availabilities() {
      this.$store.commit("liveat/reset_store_values", {
        keys: ["availabilities", "departure_date"],
      });
      this.$store.commit(
        "liveat/current_search",
        this.encode_search_params_internal(),
      );
      if (this.search_params.departure_airports.length == 0) return;
      get_availabilities_api(
        this.location.keyd,
        this.departure_airports,
        this.occupancy[0],
      ).then(
        (success) => {
          let availabilities = {};
          Object.keys(success.data.data.availabilities).forEach((av) => {
            if (success.data.data.availabilities[av].length > 0)
              availabilities = {
                ...availabilities,
                [av]: success.data.data.availabilities[av],
              };
          });
          this.$store.commit("liveat/availabilities", availabilities);
          if (this.default_departure_date) {
            /*
              la default_departure_date arriva dall'evento 'suggestion' generato dalle searchSuggestions
              se c'è una default_departure_date, allora aggiorno anche current_search e last_search per evitare che compaia la alert warning che segnala il cambio di criteri di ricerca
              la stessa cosa avviene, a prescindere da default_departure_date, dentro search() dopo ogni ricerca
              questa eccezione deve essere innescata solo al click su una delle suggestions, altrimenti va indagata più a fondo ed eventualmente ripensata              
            */
            this.$store.commit("liveat/departure_date", this.default_departure_date);
            const encoded = this.encode_search_params_internal();
            this.$store.commit("liveat/set_store_values", {
              current_search: encoded,
              last_search: encoded,
            });
          }
        },
        (error) => {
          this.log("[booking-engine] get_availabilities error", error);
          this.$store.commit("liveat/availabilities", {});
        },
      );
    },
    search() {
      this.close_engine();
      // reset search results + filters to defaults
      this.$store.commit("liveat/reset_store_values", {
        keys: [
          "products", "statics", "keyds",
          "filter_keyd", "filter_accommodation", "filter_rating",
          "filter_airport", "filter_brand", "filter_availability",
        ],
      });
      // initiate new search
      const encoded = this.encode_search_params_internal();
      this.$store.commit("liveat/set_store_values", {
        current_search: encoded,
        last_search: encoded,
        trigger_search: true,
        offset_scroll_search: 0,
      });
      this.$store.commit("searching", true);
      alpitour_rybbit_event("booking_engine:search:submit", slim_search_info(this.search_params));
      this.$emit("search", {});
    },
    on_submit_date(attrs) {
      this.$store.commit("liveat/departure_date", attrs.dep);
      this.$store.commit("liveat/nights", attrs.n);
      this.close_engine();
      //this.search()
    },
    on_destination_click() {
      this.current_tab = 1;
      if (!this.keyd_map_init && has_valid_occupancy(this.occupancy)) {
        this.keyd_map_init = true;
      }
    },
    on_submit_location(location) {
      // hidden_input click is handled inside destination.vue before emitting
      this.$store.commit("liveat/reset_store_values", {
        keys: ["availabilities", "departure_date"],
        overrides: { location },
      });
      this.$store.commit(
        "liveat/current_search",
        this.encode_search_params_internal(),
      );
      custom_event("keyd", location.label, location.keyd);
      this.current_tab = 2;
      if (!this.is_only_stay_package) {
        this.get_airports();
      }
    },
  },
};
</script>

<style lang="scss" scoped>

  .booking-engine {
    $this: &;
    position: relative;
    z-index: 100;
    max-width: 1140px;
    margin: 0 auto;

    @include media-breakpoint-down(md) {
      display: none;

      @at-root .homepage & {
        display: block;
      }
    }

    &--is-open {
      display: block;

      @include media-breakpoint-down(md) {
        margin-top: $grid-gutter-20;
        position: absolute;
        left: $grid-gutter-width;
        right: $grid-gutter-width;
        box-shadow: 0 0 15px 0 rgba($dark, .2);
      }
    }

    &__body {
      position: relative;
      z-index: 1;
      background: $extra2;
      border-radius: $border-radius-lg * 2;
      border: 1px solid $white;
      box-shadow: 0 3px 12px 0 rgba(0,0,0, 0.1), 0 1px 2px 0 rgba(0,0,0, .08);
      padding: ($grid-gutter-width / 2) ($grid-gutter-width / 4);
      @include make-grid-columns($columns: $grid-columns, $gutter: $grid-gutter-width / 2, $breakpoints: $grid-breakpoints);

      @include media-breakpoint-down(lg) {
        margin: 0;
      }

      @at-root .homepage & {
        border-radius: 0 ($border-radius-lg * 2) ($border-radius-lg * 2) ($border-radius-lg * 2);
      }
    }

    &__package-type-switch {
      display: flex;
      width: 100%;
      padding: 0 $grid-gutter-width / 2 !important;

      @include media-breakpoint-down(lg) {
        justify-content: center;
      }
    }

    // Wrappers
    &__column {
      position: static !important;
      overflow: visible;

      @include media-breakpoint-down(lg) {
        margin-bottom: $grid-gutter-width / 2
      }

      @include media-breakpoint-up(lg) {
        margin-bottom: 0;

        &.current:not(:first-child) {
          border-bottom-color: $white;
        }
      }

      &:last-child {
        margin-bottom: 0
      }
    }

    ::v-deep {
      .form {
        &-group {
          @include media-breakpoint-up(md) {
            margin-bottom: 0;
          }
        }

        &-control {
          border-color: $primary;
          font-size: $font-size-base;

          &::placeholder {
            font-size: $font-size-base;
            color: $vb-body-color;
            line-height: normal;
          }
        }
      }
    }

    .current {
      // ::v-deep to penetrate into child components (destination, occupancy, etc.)
      ::v-deep {
        .booking-engine__floating,
        .booking-engine__floating--occupancy,
        .booking-engine__floating--suggestions {
          display: block;
        }

        .booking-engine__floating--occupancy {
          .occupancy--adults, .occupancy--children {
            margin-bottom: $grid-gutter-width / 2;
          }
        }
      }
    }

    &__search {
      flex-basis: 100%;
      z-index: auto;
      height: 100%;

      &:not(.active) {
        ::v-deep input {
          background: $gray-300;
        }
      }

      ::v-deep button {
        font-weight: $vb-font-weight-semibold;
        justify-content: center;
        box-shadow: none !important;
        height: calc(1.5em + 1.25rem + 2px);
        width: 100%;
      }
    }
  }
</style>
